import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Loader2, ArrowLeft } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";

const STATUSES = [["new", "New"], ["in_progress", "In progress"], ["completed", "Completed"]];

const Login = ({ onAuth }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      sessionStorage.setItem("kag_token", data.token);
      onAuth(data);
      toast.success("Welcome back, King.");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-[var(--gold)]/40 bg-[var(--crypt)] p-8 hex-clip">
        <Wordmark />
        <h1 className="font-display text-3xl font-bold mt-8">Throne access</h1>
        <div className="mt-6 space-y-3">
          <input required type="email" data-testid="admin-email-input" className="field" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input required type="password" data-testid="admin-password-input" className="field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {err && <p data-testid="admin-login-error" className="text-sm text-[var(--blood)] mt-3">{err}</p>}
        <button disabled={busy} data-testid="admin-login-btn" className="btn-blood w-full mt-6 py-4 font-mono text-sm tracking-[0.2em] uppercase disabled:opacity-60">
          {busy ? "Entering…" : "Enter"}
        </button>
        <Link to="/" data-testid="admin-back-link" className="mt-6 flex items-center gap-2 text-sm text-[var(--ink-dim)] hover:text-[var(--gold)]"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
      </form>
    </div>
  );
};

const Row = ({ s, onStatus, onDelete }) => (
  <article data-testid={`submission-${s.id}`} className="border border-[var(--line)] bg-[var(--crypt)] p-6">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 className="font-display text-xl font-bold">{s.name}</h3>
        <a href={`mailto:${s.email}`} className="font-mono text-sm text-[var(--gold-soft)] break-all">{s.email}</a>
      </div>
      <div className="flex items-center gap-2">
        <select data-testid={`submission-status-${s.id}`} className="field !py-2 !w-auto text-sm" value={s.status} onChange={(e) => onStatus(s.id, e.target.value)}>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid={`submission-delete-${s.id}`} onClick={() => onDelete(s.id)} className="btn-ghost p-2.5" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-3 font-mono text-xs uppercase tracking-widest text-[var(--ink-dim)]">
      <span className="text-[var(--blood)]">{s.service}</span>
      {s.budget && <span>Budget: {s.budget}</span>}
      <span>{new Date(s.created_at).toLocaleString()}</span>
    </div>
    <p className="mt-4 text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{s.details}</p>
  </article>
);

const Dashboard = ({ onLogout }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/submissions");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/submissions/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/submissions/${id}`); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const shown = (items || []).filter((x) => filter === "all" || x.status === filter);

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-10 py-10">
      <div className="flex items-center justify-between">
        <Link to="/" data-testid="dashboard-home-link"><Wordmark /></Link>
        <button data-testid="admin-logout-btn" onClick={onLogout} className="btn-ghost px-4 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold mt-12">Commission requests</h1>
      <div className="mt-8 flex flex-wrap gap-2">
        {[["all", "All"], ...STATUSES].map(([v, l]) => (
          <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-colors ${filter === v ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--line)] text-[var(--ink-dim)]"}`}>
            {l} ({v === "all" ? (items || []).length : (items || []).filter((x) => x.status === v).length})
          </button>
        ))}
      </div>
      <div className="mt-8 space-y-4" data-testid="submissions-list">
        {items === null && <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" />}
        {items && shown.length === 0 && <p data-testid="submissions-empty" className="text-[var(--ink-dim)]">No requests here yet.</p>}
        {shown.map((s) => <Row key={s.id} s={s} onStatus={setStatus} onDelete={del} />)}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    sessionStorage.removeItem("kag_token");
    setUser(false);
  };
  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  return user ? <Dashboard onLogout={logout} /> : <Login onAuth={setUser} />;
}
